import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { directoryApi } from "@/core/api/directoryApi";
import type {
  RoleDataScope,
  TenantPermissionModule,
  TenantRole,
  UpsertTenantRoleRequest,
} from "@/core/types/directory";
import { AppIcon } from "@/shared/components/AppIcon";

interface RoleDraft {
  name: string;
  isActive: boolean;
  grants: Record<string, RoleDataScope>;
}

const scopeLabels: Record<RoleDataScope, string> = {
  explicit: "موارد انتخاب‌شده",
  own: "فقط موارد خودش",
  team: "موارد تیم",
  branch: "موارد شعبه",
  tenant: "تمام موارد سازمان",
};

const editableScopes: RoleDataScope[] = ["own", "tenant"];
const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : "عملیات ناموفق بود.";

const createDraft = (
  modules: TenantPermissionModule[],
  role?: TenantRole,
): RoleDraft => {
  const grants: Record<string, RoleDataScope> = Object.fromEntries(
    role?.permissions.map((grant) => [grant.code, grant.scope]) ?? [],
  );
  modules
    .filter((module) => module.isCore)
    .forEach((module) => {
      module.permissions.forEach((permission) => {
        grants[permission.code] ??= "tenant";
      });
    });
  return { name: role?.name ?? "", isActive: role?.isActive ?? true, grants };
};

export const TenantRolesPage = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [editingRole, setEditingRole] = useState<TenantRole | "new" | null>(
    null,
  );
  const [draft, setDraft] = useState<RoleDraft>({
    name: "",
    isActive: true,
    grants: {},
  });
  const rolesQuery = useQuery({
    queryKey: ["tenant-roles"],
    queryFn: directoryApi.roles,
  });
  const modulesQuery = useQuery({
    queryKey: ["tenant-roles", "permission-modules"],
    queryFn: directoryApi.permissionModules,
  });
  const roles = rolesQuery.data ?? [];
  const modules = modulesQuery.data ?? [];

  const openEditor = (role?: TenantRole): void => {
    setDraft(createDraft(modules, role));
    setEditingRole(role ?? "new");
  };
  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ["tenant-roles"] });
  const saveRole = useMutation({
    mutationFn: async () => {
      const request: UpsertTenantRoleRequest = {
        name: draft.name.trim(),
        isActive: draft.isActive,
        permissions: Object.entries(draft.grants).map(([code, scope]) => ({
          code,
          scope,
        })),
      };
      if (editingRole === "new") return directoryApi.createRole(request);
      return directoryApi.updateRole(editingRole!.id, request);
    },
    onSuccess: async () => {
      setEditingRole(null);
      await refresh();
    },
  });
  const deleteRole = useMutation({
    mutationFn: directoryApi.deleteRole,
    onSuccess: refresh,
  });
  const totalAssigned = useMemo(
    () => roles.reduce((total, role) => total + role.memberCount, 0),
    [roles],
  );

  const togglePermission = (
    module: TenantPermissionModule,
    code: string,
    selected: boolean,
  ): void => {
    if (module.isCore) return;
    setDraft((current) => {
      const grants = { ...current.grants };
      if (selected)
        grants[code] =
          grants[code] ?? (module.code === "LEADS" ? "own" : "tenant");
      else delete grants[code];
      return { ...current, grants };
    });
  };

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-bold text-brand-600">
            مدیریت دسترسی سازمان
          </p>
          <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">
            نقش‌ها و دسترسی‌ها
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">
            برای هر عنوان شغلی یک نقش بسازید و مشخص کنید پرسنل به کدام بخش‌ها
            دسترسی داشته باشند. فقط ماژول‌های فعال اشتراک شما قابل انتخاب‌اند.
          </p>
        </div>
        <button
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white shadow-lg shadow-brand-500/20 disabled:opacity-50"
          disabled={modulesQuery.isLoading}
          onClick={() => openEditor()}
          type="button"
        >
          <AppIcon name="plus" size={18} /> نقش جدید
        </button>
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">نقش‌های فعال</p>
          <p className="mt-2 text-2xl font-black text-ink-950">
            {new Intl.NumberFormat("fa-IR").format(
              roles.filter((role) => role.isActive).length,
            )}
          </p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">پرسنل تخصیص‌یافته</p>
          <p className="mt-2 text-2xl font-black text-violet-600">
            {new Intl.NumberFormat("fa-IR").format(totalAssigned)}
          </p>
        </article>
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-bold text-slate-500">
            ماژول‌های قابل کنترل
          </p>
          <p className="mt-2 text-2xl font-black text-emerald-600">
            {new Intl.NumberFormat("fa-IR").format(
              modules.filter((module) => !module.isCore).length,
            )}
          </p>
        </article>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-black text-ink-950">فهرست نقش‌ها</h2>
            <p className="mt-1 text-sm text-slate-400">
              نقش‌های سیستمی برای حفظ امنیت سازمان قابل ویرایش نیستند.
            </p>
          </div>
          <span className="rounded-full bg-brand-50 px-3 py-1.5 text-xs font-black text-brand-600">
            {new Intl.NumberFormat("fa-IR").format(roles.length)} نقش
          </span>
        </div>
        {rolesQuery.isLoading ? (
          <div className="grid min-h-48 place-items-center text-sm font-bold text-slate-400">
            در حال دریافت نقش‌ها…
          </div>
        ) : rolesQuery.isError ? (
          <div className="mt-5 rounded-2xl bg-rose-50 p-4 text-sm font-bold text-rose-700">
            {errorText(rolesQuery.error)}
          </div>
        ) : (
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            {roles.map((role) => (
              <article
                className="rounded-2xl border border-slate-100 p-5"
                key={role.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black text-slate-800">{role.name}</h3>
                      {role.isSystemRole ? (
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-500">
                          سیستمی
                        </span>
                      ) : null}
                      {!role.isActive ? (
                        <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-black text-amber-600">
                          غیرفعال
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs text-slate-400" dir="ltr">
                      {role.code}
                    </p>
                  </div>
                  <span className="grid size-10 place-items-center rounded-xl bg-violet-50 text-violet-600">
                    <AppIcon name="settings" size={19} />
                  </span>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-[11px] font-bold text-slate-400">
                      مجوز فعال
                    </p>
                    <p className="mt-1 font-black text-slate-700">
                      {new Intl.NumberFormat("fa-IR").format(
                        role.permissions.length,
                      )}
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-[11px] font-bold text-slate-400">
                      پرسنل این نقش
                    </p>
                    <p className="mt-1 font-black text-slate-700">
                      {new Intl.NumberFormat("fa-IR").format(role.memberCount)}{" "}
                      نفر
                    </p>
                  </div>
                </div>
                {role.isSystemRole ? (
                  <p className="mt-4 text-xs leading-6 text-slate-400">
                    این نقش توسط سیستم مدیریت می‌شود. برای دسترسی اختصاصی، یک
                    نقش جدید بسازید.
                  </p>
                ) : (
                  <div className="mt-4 flex gap-2">
                    <button
                      className="flex-1 rounded-xl border border-brand-100 py-2.5 text-xs font-black text-brand-600 hover:bg-brand-50"
                      onClick={() => openEditor(role)}
                      type="button"
                    >
                      ویرایش دسترسی
                    </button>
                    <button
                      className="rounded-xl border border-rose-100 px-3 py-2.5 text-xs font-black text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={role.memberCount > 0 || deleteRole.isPending}
                      onClick={() => {
                        if (window.confirm(`نقش «${role.name}» حذف شود؟`))
                          deleteRole.mutate(role.id);
                      }}
                      title={
                        role.memberCount > 0
                          ? "ابتدا پرسنل این نقش را به نقش دیگری منتقل کنید."
                          : "حذف نقش"
                      }
                      type="button"
                    >
                      حذف
                    </button>
                  </div>
                )}
              </article>
            ))}
            {roles.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm font-bold text-slate-400 lg:col-span-2">
                هنوز نقشی تعریف نشده است.
              </div>
            ) : null}
          </div>
        )}
        {deleteRole.isError ? (
          <div className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
            {errorText(deleteRole.error)}
          </div>
        ) : null}
      </section>

      {editingRole ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/40 sm:place-items-center sm:p-4">
          <button
            aria-label="بستن"
            className="absolute inset-0"
            onClick={() => setEditingRole(null)}
            type="button"
          />
          <section className="relative z-10 max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-4xl sm:rounded-3xl sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-ink-950">
                  {editingRole === "new" ? "تعریف نقش جدید" : "ویرایش نقش"}
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-400">
                  مجوز هر عملیات را فعال کنید و دامنه اطلاعات قابل مشاهده را
                  تعیین کنید.
                </p>
              </div>
              <button
                className="grid size-9 place-items-center rounded-xl bg-slate-50 text-slate-500"
                onClick={() => setEditingRole(null)}
                type="button"
              >
                <AppIcon name="close" size={18} />
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto]">
              <label>
                <span className="mb-2 block text-xs font-bold text-slate-500">
                  نام نقش
                </span>
                <input
                  autoFocus
                  className="input"
                  maxLength={120}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="مثلاً مسئول پیگیری فروش"
                  value={draft.name}
                />
              </label>
              <label className="flex items-end gap-2 pb-3 text-sm font-bold text-slate-600">
                <input
                  checked={draft.isActive}
                  className="size-4 accent-brand-600"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      isActive: event.target.checked,
                    }))
                  }
                  type="checkbox"
                />{" "}
                فعال باشد
              </label>
            </div>
            <div className="mt-6 space-y-4">
              {modulesQuery.isLoading ? (
                <div className="rounded-2xl bg-slate-50 p-5 text-sm font-bold text-slate-400">
                  در حال آماده‌سازی مجوزها…
                </div>
              ) : (
                modules.map((module) => (
                  <section
                    className="rounded-2xl border border-slate-100"
                    key={module.code}
                  >
                    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-black text-slate-800">
                          {module.title}
                        </h3>
                        {module.isCore ? (
                          <span className="rounded-full bg-brand-50 px-2 py-1 text-[10px] font-black text-brand-600">
                            الزامی
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-slate-400">
                        {module.description}
                      </p>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {module.permissions.map((permission) => {
                        const selected =
                          module.isCore ||
                          Boolean(draft.grants[permission.code]);
                        const supportsRecordScope = module.code === "LEADS";
                        const scope = draft.grants[permission.code] ?? "tenant";
                        return (
                          <div
                            className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                            key={permission.code}
                          >
                            <label className="flex min-w-0 items-start gap-3">
                              <input
                                checked={selected}
                                className="mt-1 size-4 shrink-0 accent-brand-600"
                                disabled={module.isCore}
                                onChange={(event) =>
                                  togglePermission(
                                    module,
                                    permission.code,
                                    event.target.checked,
                                  )
                                }
                                type="checkbox"
                              />
                              <span>
                                <span className="block text-sm font-black text-slate-700">
                                  {permission.title}
                                </span>
                                <span className="mt-1 block text-xs leading-5 text-slate-400">
                                  {permission.description}
                                </span>
                              </span>
                            </label>
                            <select
                              aria-label={`دامنه ${permission.title}`}
                              className="input w-full shrink-0 bg-white text-xs sm:w-48"
                              disabled={
                                !selected ||
                                module.isCore ||
                                !supportsRecordScope
                              }
                              onChange={(event) =>
                                setDraft((current) => ({
                                  ...current,
                                  grants: {
                                    ...current.grants,
                                    [permission.code]: event.target
                                      .value as RoleDataScope,
                                  },
                                }))
                              }
                              value={supportsRecordScope ? scope : "tenant"}
                            >
                              {supportsRecordScope ? (
                                editableScopes.map((item) => (
                                  <option key={item} value={item}>
                                    {scopeLabels[item]}
                                  </option>
                                ))
                              ) : (
                                <option value="tenant">
                                  دسترسی در سطح سازمان
                                </option>
                              )}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))
              )}
            </div>
            {saveRole.isError ? (
              <div className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
                {errorText(saveRole.error)}
              </div>
            ) : null}
            <div className="mt-7 flex gap-3">
              <button
                className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600"
                onClick={() => setEditingRole(null)}
                type="button"
              >
                انصراف
              </button>
              <button
                className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-50"
                disabled={saveRole.isPending || !draft.name.trim()}
                onClick={() => saveRole.mutate()}
                type="button"
              >
                {saveRole.isPending ? "در حال ذخیره…" : "ذخیره نقش"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};
